import React from 'react';
import { GeoJsonLayer, TextLayer } from '@deck.gl/layers';
import { DeckGL } from '@deck.gl/react';
import { WebMercatorViewport, FlyToInterpolator, type Layer } from '@deck.gl/core';
import { Loader2, Maximize2 } from 'lucide-react';
import { heatRamp, GEO_MAP_HEIGHT } from './reportTheme';

// CTV-445: shared national choropleth used by both the State and DMA report
// maps. Renders a deck.gl GeoJsonLayer colored by a per-region value (plays) on
// the report's heat scale (the brand color's ramp, or the default green), with
// hover tooltips and a legend. The caller supplies AK/HI/PR-transformed GeoJSON,
// a value-by-key map, and accessors that pull each feature's key and name.

const NO_DATA_FILL: [number, number, number, number] = [238, 242, 246, 255]; // slate-100/200

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
const lerp = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);

// Sequential heat color for t in [0,1] across the campaign's play range, between
// the supplied low/mid/high ramp stops.
function heatColor(
  t: number,
  low: [number, number, number],
  mid: [number, number, number],
  high: [number, number, number],
): [number, number, number] {
  const c = Math.max(0, Math.min(1, t));
  if (c <= 0.5) {
    const k = c / 0.5;
    return [lerp(low[0], mid[0], k), lerp(low[1], mid[1], k), lerp(low[2], mid[2], k)];
  }
  const k = (c - 0.5) / 0.5;
  return [lerp(mid[0], high[0], k), lerp(mid[1], high[1], k), lerp(mid[2], high[2], k)];
}

export interface ChoroplethLabel {
  position: [number, number];
  text: string;
  key: string; // value-by-key key, for label-color contrast
}

// Walk a feature's coordinate tree, expanding [minLng, minLat, maxLng, maxLat].
function expandBbox(geom: GeoJSON.Geometry, bbox: [number, number, number, number]): void {
  const visit = (c: unknown): void => {
    if (typeof (c as number[])[0] === 'number') {
      const [lng, lat] = c as [number, number];
      if (lng < bbox[0]) bbox[0] = lng;
      if (lat < bbox[1]) bbox[1] = lat;
      if (lng > bbox[2]) bbox[2] = lng;
      if (lat > bbox[3]) bbox[3] = lat;
      return;
    }
    for (const child of c as unknown[]) visit(child);
  };
  if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') visit(geom.coordinates);
}

interface NationalChoroplethProps {
  // Already AK/HI/PR-transformed FeatureCollection (null while loading).
  geojson: GeoJSON.FeatureCollection | null;
  valueByKey: Map<string, number>;
  keyOf: (f: GeoJSON.Feature) => string | null;
  nameOf: (f: GeoJSON.Feature) => string;
  labels?: ChoroplethLabel[];
  // Draw a white pill behind each label so it stays readable when a long name
  // overruns its region's border onto a lighter neighbor (DMA names). Off by
  // default — short labels (state abbreviations) sit cleanly inside their fill.
  labelBackground?: boolean;
  idPrefix: string; // unique deck.gl layer ids
  height?: number;
  zoom?: number;
  // Max zoom-in for the controller, the focus fit, and the hover auto-zoom.
  // States are large, so the State map passes a lower cap than the DMA map.
  maxZoom?: number;
  // When set, the initial view fits the features whose key is in this list
  // (e.g. the delivered DMAs) instead of the national framing.
  focusKeys?: string[];
  // Brand color; the heat scale is derived from it (falls back to green).
  accentColor?: string;
  // Key of the region to outline (driven by hovering its row in the breakdown).
  highlightKey?: string | null;
  // Outline color for the highlighted region (falls back to a green).
  highlightColor?: string;
}

const NationalChoropleth: React.FC<NationalChoroplethProps> = ({
  geojson,
  valueByKey,
  keyOf,
  nameOf,
  labels = [],
  labelBackground = false,
  idPrefix,
  height = GEO_MAP_HEIGHT,
  zoom = 2.5,
  maxZoom = 6,
  focusKeys,
  accentColor,
  highlightKey,
  highlightColor,
}) => {
  const maxValue = React.useMemo(() => Math.max(1, ...valueByKey.values()), [valueByKey]);
  const highlightRgb = React.useMemo(
    () => hexToRgb(/^#[0-9a-f]{6}$/i.test(highlightColor ?? '') ? (highlightColor as string) : '#16a34a'),
    [highlightColor],
  );

  // Heat ramp from the brand color (or the default green), as both hex (legend)
  // and rgb tuples (deck.gl fill).
  const ramp = React.useMemo(() => heatRamp(accentColor), [accentColor]);
  const rampRgb = React.useMemo(
    () => ({ low: hexToRgb(ramp.low), mid: hexToRgb(ramp.mid), high: hexToRgb(ramp.high) }),
    [ramp],
  );

  // Track the map's actual rendered width so the view fits correctly at every
  // screen size (deck.gl doesn't auto-refit to its container). Measured before
  // paint via useLayoutEffect, then kept current with a ResizeObserver.
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    setContainerWidth(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Fit the view to the actual width. focusKeys set (DMA) → frame just those
  // markets (with a zoom cap so a single small one still shows context); no
  // focusKeys (state national view) → frame the whole map, capped near the
  // tuned default zoom so wide screens keep that look and only narrow screens
  // zoom out to avoid clipping.
  const fitViewState = React.useMemo(() => {
    // Lock zoom-out to the initial framing: the starting view already shows all
    // the delivered regions, so there's no reason to zoom out further (and no
    // reason to shrink the US to a dot). maxZoom caps zoom-in at region detail.
    const national = { longitude: -97, latitude: 37, zoom, minZoom: zoom, maxZoom };
    if (!geojson || geojson.features.length === 0) return national;
    const isFocus = !!(focusKeys && focusKeys.length > 0);
    const focus = isFocus ? new Set(focusKeys) : null;
    const bbox: [number, number, number, number] = [180, 90, -180, -90];
    for (const f of geojson.features) {
      if (focus) {
        const k = keyOf(f);
        if (!k || !focus.has(k)) continue;
      }
      if (f.geometry) expandBbox(f.geometry, bbox);
    }
    if (bbox[0] > bbox[2] || bbox[1] > bbox[3]) return national;
    try {
      const fit = new WebMercatorViewport({ width: containerWidth || 600, height }).fitBounds(
        [
          [bbox[0], bbox[1]],
          [bbox[2], bbox[3]],
        ],
        { padding: isFocus ? 56 : 32 },
      );
      const cap = isFocus ? maxZoom : zoom; // national: never zoom in past the tuned default
      const finalZoom = Math.min(fit.zoom, cap);
      // minZoom = the initial zoom so the user can pan/zoom-in but not zoom out
      // past the starting frame.
      return { longitude: fit.longitude, latitude: fit.latitude, zoom: finalZoom, minZoom: finalZoom, maxZoom };
    } catch {
      return national;
    }
  }, [geojson, focusKeys, keyOf, zoom, height, containerWidth, maxZoom]);

  // Bounds the user may pan within: the delivered region (focus) — or the whole
  // US for the national view — plus a small margin, so the map can't be dragged
  // off into empty space. deck.gl has no native maxBounds, so we clamp manually.
  const clampBounds = React.useMemo<[number, number, number, number] | null>(() => {
    if (!geojson || geojson.features.length === 0) return null;
    const focus = focusKeys && focusKeys.length > 0 ? new Set(focusKeys) : null;
    const bb: [number, number, number, number] = [180, 90, -180, -90];
    for (const f of geojson.features) {
      if (focus) {
        const k = keyOf(f);
        if (!k || !focus.has(k)) continue;
      }
      if (f.geometry) expandBbox(f.geometry, bb);
    }
    if (bb[0] > bb[2]) return null;
    const m = 2; // degrees of context allowed beyond the content
    return [bb[0] - m, bb[1] - m, bb[2] + m, bb[3] + m];
  }, [geojson, focusKeys, keyOf]);

  // Keep the viewport inside clampBounds (maxBounds-style): center on the bounds
  // when the viewport is wider/taller than them, else clamp the edges.
  const clampToBounds = React.useCallback(
    (vs: Record<string, unknown>): Record<string, unknown> => {
      if (!clampBounds) return vs;
      const w = containerWidth || 600;
      try {
        const vp = new WebMercatorViewport({
          longitude: vs.longitude as number,
          latitude: vs.latitude as number,
          zoom: vs.zoom as number,
          width: w,
          height,
        });
        const [west, north] = vp.unproject([0, 0]) as [number, number];
        const [east, south] = vp.unproject([w, height]) as [number, number];
        const halfLng = Math.abs(east - west) / 2;
        const halfLat = Math.abs(north - south) / 2;
        const [bw, bs, be, bn] = clampBounds;
        const longitude = be - bw <= 2 * halfLng ? (bw + be) / 2 : Math.min(Math.max(vs.longitude as number, bw + halfLng), be - halfLng);
        const latitude = bn - bs <= 2 * halfLat ? (bs + bn) / 2 : Math.min(Math.max(vs.latitude as number, bs + halfLat), bn - halfLat);
        return { ...vs, longitude, latitude };
      } catch {
        return vs;
      }
    },
    [clampBounds, containerWidth, height],
  );

  // Controlled view so we can pan to a highlighted region. Seeded from the
  // computed fit; reset to it when the delivered footprint changes; user
  // pan/zoom captured via onViewStateChange.
  const fitRef = React.useRef(fitViewState);
  fitRef.current = fitViewState;
  const [view, setView] = React.useState<Record<string, number>>(fitViewState);
  const viewRef = React.useRef(view);
  viewRef.current = view;
  // Re-frame to the computed fit when the footprint (or measured width) changes;
  // user pan/zoom is preserved between those.
  const fitKey = `${(focusKeys ?? []).join(',')}:${geojson ? geojson.features.length : 0}:${Math.round(containerWidth)}`;
  React.useEffect(() => {
    setView(fitRef.current);
  }, [fitKey]);

  // Re-frame to the default fit, animated.
  const resetView = () =>
    setView({
      ...fitRef.current,
      transitionDuration: 500,
      transitionInterpolator: new FlyToInterpolator() as unknown as number,
    });

  // When a region is highlighted from the list, zoom/pan to frame it — unless
  // it's already comfortably on screen (avoid jarring re-zoom between rows).
  React.useEffect(() => {
    if (!highlightKey || !geojson) return;
    const f = geojson.features.find((x) => keyOf(x) === highlightKey);
    if (!f?.geometry) return;
    const bb: [number, number, number, number] = [180, 90, -180, -90];
    expandBbox(f.geometry, bb);
    if (bb[0] > bb[2]) return;
    try {
      const w = containerWidth || 600;
      const vp = new WebMercatorViewport({ ...viewRef.current, width: w, height });
      const tl = vp.project([bb[0], bb[3]]) as [number, number]; // nw
      const br = vp.project([bb[2], bb[1]]) as [number, number]; // se
      const cx = (tl[0] + br[0]) / 2;
      const cy = (tl[1] + br[1]) / 2;
      const frac = Math.max(Math.abs(br[0] - tl[0]), Math.abs(br[1] - tl[1])) / Math.min(w, height);
      // Skip only if already roughly centered + well-sized, so the region the
      // hover ends on always flies to center (and isn't skipped just for being
      // somewhere on screen).
      const centered = cx >= 0.3 * w && cx <= 0.7 * w && cy >= 0.3 * height && cy <= 0.7 * height;
      if (centered && frac >= 0.15 && frac <= 0.85) return;
      const fit = new WebMercatorViewport({ width: w, height }).fitBounds(
        [
          [bb[0], bb[1]],
          [bb[2], bb[3]],
        ],
        { padding: 60, maxZoom },
      );
      const minZ = fitRef.current.minZoom ?? 2;
      const maxZ = maxZoom;
      setView((v) => ({
        ...v,
        longitude: fit.longitude,
        latitude: fit.latitude,
        zoom: Math.max(minZ, Math.min(fit.zoom, maxZ)),
        // Animate the move (like the ZIP map's easeTo) rather than snapping.
        transitionDuration: 400,
        transitionInterpolator: new FlyToInterpolator() as unknown as number,
      }));
    } catch {
      /* viewport math can throw on degenerate states — ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightKey]);

  const layers = React.useMemo(() => {
    const result: Layer[] = [];
    if (!geojson) return result;
    result.push(
      new GeoJsonLayer({
        id: `${idPrefix}-fill`,
        data: geojson,
        filled: true,
        stroked: true,
        pickable: true,
        getFillColor: (f: GeoJSON.Feature) => {
          const plays = valueByKey.get(keyOf(f) ?? '') ?? 0;
          if (plays <= 0) return NO_DATA_FILL;
          return [...heatColor(plays / maxValue, rampRgb.low, rampRgb.mid, rampRgb.high), 235];
        },
        getLineColor: [255, 255, 255, 255],
        getLineWidth: 1,
        lineWidthUnits: 'pixels' as const,
        updateTriggers: { getFillColor: [valueByKey, maxValue, rampRgb] },
      }),
    );
    if (labels.length > 0) {
      result.push(
        new TextLayer({
          id: `${idPrefix}-labels`,
          data: labels,
          getPosition: (d: { position: number[] }) => d.position as [number, number],
          getText: (d: { text: string }) => d.text,
          getSize: 10,
          // With a background pill the text always sits on white, so a constant
          // slate reads everywhere — even where a long name overruns its border.
          // Without one, fall back to white-on-dark / slate-on-light contrast.
          getColor: labelBackground
            ? [51, 65, 85, 255] // slate-700
            : (d: { key: string }) =>
                (valueByKey.get(d.key) ?? 0) / maxValue > 0.45 ? [255, 255, 255, 255] : [100, 116, 139, 255],
          fontWeight: 600,
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          sizeUnits: 'pixels' as const,
          billboard: false,
          ...(labelBackground
            ? {
                background: true,
                getBackgroundColor: [255, 255, 255, 230] as [number, number, number, number],
                backgroundPadding: [4, 2] as [number, number],
                getBorderColor: [203, 213, 225, 230] as [number, number, number, number], // slate-300
                getBorderWidth: 0.5,
              }
            : {}),
          updateTriggers: { getColor: [valueByKey, maxValue, labelBackground] },
        }),
      );
    }
    // Outline the region hovered in the breakdown list — a white casing under an
    // accent line so it reads on any fill shade, drawn on top of everything.
    if (highlightKey) {
      const hl = geojson.features.filter((f) => keyOf(f) === highlightKey);
      if (hl.length > 0) {
        const hlData: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: hl };
        result.push(
          new GeoJsonLayer({
            id: `${idPrefix}-hl-casing`,
            data: hlData,
            filled: false,
            stroked: true,
            pickable: false,
            getLineColor: [255, 255, 255, 255],
            getLineWidth: 4,
            lineWidthUnits: 'pixels' as const,
          }),
          new GeoJsonLayer({
            id: `${idPrefix}-hl`,
            data: hlData,
            filled: false,
            stroked: true,
            pickable: false,
            getLineColor: [...highlightRgb, 255],
            getLineWidth: 2,
            lineWidthUnits: 'pixels' as const,
          }),
        );
      }
    }
    return result;
  }, [geojson, labels, labelBackground, valueByKey, maxValue, keyOf, idPrefix, rampRgb, highlightKey, highlightRgb]);

  // The container always renders (with the ref) so its width is measured even
  // while the geometry loads; the map + legend swap in once geojson is ready.
  return (
    <div
      ref={containerRef}
      className="rounded-lg border border-slate-200 overflow-hidden relative bg-white"
      style={{ height }}
    >
      {!geojson ? (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-50 text-[12px] text-slate-400">
          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
          Loading map…
        </div>
      ) : (
        <>
          <DeckGL
            // Controlled view so the user can pan/zoom AND we can pan to a
            // highlighted region. Rotation/pitch stay off to keep the map flat.
            viewState={view}
            onViewStateChange={(params: { viewState: Record<string, unknown>; interactionState?: { inTransition?: boolean } }) => {
              // Skip clamping mid-fly (the highlight transition targets valid
              // regions); clamp on user pan/zoom so the map stays over content.
              const vs = params.interactionState?.inTransition ? params.viewState : clampToBounds(params.viewState);
              setView({
                ...(vs as Record<string, number>),
                minZoom: fitRef.current.minZoom,
                maxZoom: fitRef.current.maxZoom,
              });
            }}
            controller={{
              scrollZoom: true,
              dragPan: true,
              dragRotate: false,
              doubleClickZoom: true,
              touchZoom: true,
              touchRotate: false,
              keyboard: true,
            }}
            layers={layers}
            style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', background: '#ffffff' }}
            getCursor={({ isHovering }: { isHovering: boolean }) => (isHovering ? 'pointer' : 'default')}
            getTooltip={({ object }: { object?: GeoJSON.Feature }) => {
              if (!object) return null;
              const plays = valueByKey.get(keyOf(object) ?? '') ?? 0;
              // Only regions that actually delivered get a tooltip — don't label
              // every empty state/DMA with "0 plays".
              if (plays <= 0) return null;
              return {
                html: `<div style="font-weight:600">${nameOf(object)}</div><div>${plays.toLocaleString('en-US')} plays</div>`,
                style: {
                  backgroundColor: '#ffffff',
                  color: '#0f172a',
                  fontSize: '11px',
                  padding: '6px 8px',
                  borderRadius: '6px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                },
              };
            }}
          />
          <button
            type="button"
            onClick={resetView}
            className="absolute top-2 right-2 z-10 bg-white/90 backdrop-blur-sm rounded-md px-2 py-1 shadow-sm border border-slate-200 text-[10px] font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            title="Reset view"
          >
            <Maximize2 className="w-3 h-3" />
            Reset view
          </button>
          <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm rounded-md px-2.5 py-1.5 shadow-sm border border-slate-200 flex items-center gap-2">
            <span className="text-[10px] text-slate-500">Fewer</span>
            <div className="h-2 w-20 rounded-full" style={{ background: `linear-gradient(to right, ${ramp.low}, ${ramp.high})` }} />
            <span className="text-[10px] text-slate-500">More plays</span>
          </div>
        </>
      )}
    </div>
  );
};

export default NationalChoropleth;
